/**
 * Login_Screen.tsx — SK Platform
 * Premium, animated sign-in. Reuses the shared auth components (EmailComponent,
 * PasswordInput, PrimaryButton); all API calls go through Login_Service →
 * api-endpoints (no hardcoded URLs).
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Animated,
} from 'react-native';

// ── Components ────────────────────────────────────────────────────────────
import EmailComponent from '../../components/emailInput';
import PrimaryButton from '../../components/primaryButton';
import PasswordInput from '../../components/passwordInput';

// ── Navigation ──────────────────────────────────────────────────────────────
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { useAuthFlow } from '../../navigation/authFlowContext';
import { takePendingInvite } from '../../navigation/pendingInvite';

// ── Service layer ─────────────────────────────────────────────────────────
import { loginOrg } from '../../services/auth/login';

// ── Handlers ──────────────────────────────────────────────────────────────
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useToast } from '../../components/toast';

// ── Draft persistence (keeps typed email/password across navigation) ────────
import { getLoginDraft, setLoginDraft, clearLoginDraft } from './loginDraft';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// ── Theme ─────────────────────────────────────────────────────────────────
import { useTheme } from '../../context/themeContext';
import AuthBrandHeader from '../../components/authBrandHeader';
import AuthSplitLayout, { useAuthPanelVisible } from '../../components/authSplitLayout';

const Login_Screen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'login'>>();
  const { theme } = useTheme();
  const { colors } = theme;
  // The black panel carries the brand on wide screens, so the card only shows
  // its own header when the panel is absent (mobile). See authSplitLayout.
  const showCardBrand = !useAuthPanelVisible();
  const { enterApp } = useAuthFlow();
  const toast = useToast();

  // Restore any in-progress draft so email/password survive navigating away.
  const savedDraft = getLoginDraft();
  const [email, setEmail] = useState(savedDraft?.email ?? '');
  const [emailValid, setEmailValid] = useState(EMAIL_RE.test(savedDraft?.email ?? ''));
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState(savedDraft?.password ?? '');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Inline error shows only after the field is blurred empty — not while typing.
  const passwordError = passwordTouched && password.length === 0 ? 'Password is required' : undefined;

  // Persist the draft on every change; cleared on successful sign-in.
  useEffect(() => {
    setLoginDraft({ email, password });
  }, [email, password]);

  // Card entrance animation (fade + rise).
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(18)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 320, useNativeDriver: true }),
      Animated.spring(rise, { toValue: 0, friction: 8, tension: 60, useNativeDriver: true }),
    ]).start();
  }, [fade, rise]);

  const handleSignIn = async () => {
    if (isLoading) return;
    // Mark both fields touched so the inline red error states (border + helper
    // text) show on press even when nothing has been typed yet.
    setEmailTouched(true);
    setPasswordTouched(true);
    // Client-side validation on press — button stays enabled; surface the first
    // problem via toast and don't hit the server until it's valid.
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!emailValid) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (password.length === 0) {
      toast.error('Password is required');
      return;
    }
    setIsLoading(true);
    try {
      const loginRes = await loginOrg(email, password);
      // MFA-protected account → the platform returns { kind:'mfa_required',
      // mfa_token } instead of a session. Route to the 6-digit challenge.
      const mfa = (loginRes as any)?.kind === 'mfa_required'
        ? (loginRes as any)
        : (loginRes as any)?.data?.kind === 'mfa_required'
          ? (loginRes as any).data
          : null;
      if (mfa) {
        navigation.navigate('mfaChallenge', { mfaToken: mfa.mfa_token, email });
        return;
      }
      // Unverified account → route to resend-verification (org inactive until verified).
      if (loginRes?.data?.verification_required) {
        navigation.navigate('resendVerification', { email });
        return;
      }
      // Verified but the profile is still blank → resume onboarding on the
      // "Tell us about you" card instead of entering the shell.
      //
      // Only LEGACY accounts reach this. Signup now collects the whole profile
      // in one form, so those accounts come back profile_complete:true and go
      // straight to the shell; this branch serves users created before that
      // change (and invite-accepts) who still owe us their details.
      if (loginRes?.data?.profile_complete === false) {
        clearLoginDraft();
        navigation.navigate('personalInfo');
        return;
      }
      // Signed in — drop the draft so a future visit starts clean.
      clearLoginDraft();
      showSuccess(loginRes, 'Login successful', 'Welcome back!');
      // Were they part-way through accepting an invitation when we asked them to
      // sign in? Land them back on it rather than in the shell — signing in was
      // our detour, not their goal. Reads once and clears, so an ordinary sign-in
      // later is unaffected.
      const pendingInvite = takePendingInvite();
      if (pendingInvite) {
        navigation.navigate('invitationAccept', { token: pendingInvite });
        return;
      }
      enterApp();
    } catch (err) {
      handleApiError(err, 'Login Failed', 'Login failed. Check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSplitLayout>
          <Animated.View
            style={[
              styles.card,
              { backgroundColor: colors.card, borderColor: colors.line, opacity: fade, transform: [{ translateY: rise }] },
            ]}
          >
            {showCardBrand ? <AuthBrandHeader /> : null}

            <Text style={[styles.welcomeTitle, { color: colors.ink }]}>Welcome to Softknack</Text>
            <Text style={[styles.welcomeSub, { color: colors.ink2 }]}>
              Sign in to your AI-powered workspace — sales, support, hiring, and more, in one place.
            </Text>

            <View style={styles.form}>
              {/* Email */}
              <EmailComponent
                required
                value={email}
                touched={emailTouched}
                onValueChange={(val, valid) => {
                  setEmail(val);
                  setEmailValid(valid);
                }}
                onBlur={() => setEmailTouched(true)}
                onSubmitEditing={() => {}}
              />

              {/* Password */}
              <PasswordInput
                label="Password"
                required
                placeholder="Enter your password"
                value={password}
                onChangeText={setPassword}
                touched={passwordTouched}
                error={passwordError}
                mode="login"
                onBlur={() => setPasswordTouched(true)}
                onSubmitEditing={handleSignIn}
              />

              {/* Forgot password — premium accent pill link */}
              <View style={styles.forgotRow}>
                <PrimaryButton
                  title="Forgot password?"
                  onPress={() => navigation.navigate('forgotPassword')}
                  variant="ghost"
                  size="small"
                  style={styles.forgotBtn}
                  textStyle={[styles.forgotBtnText, { color: colors.accent }]}
                />
              </View>

              {/* Sign In button */}
              <PrimaryButton
                title="Sign In  →"
                onPress={handleSignIn}
                loading={isLoading}
                hoverLift
                style={styles.signInBtn}
                textStyle={styles.signInBtnText}
              />

              {/* Footer — premium divider + sign-up link */}
              <View style={styles.footerDivider}>
                <View style={[styles.footerRule, { backgroundColor: colors.line }]} />
                <Text style={[styles.footerDividerText, { color: colors.ink4 }]}>NEW HERE?</Text>
                <View style={[styles.footerRule, { backgroundColor: colors.line }]} />
              </View>
              <View style={styles.footer}>
                <Text style={[styles.footerText, { color: colors.ink3 }]}>Don't have an account? </Text>
                <PrimaryButton
                  title="Create one  →"
                  onPress={() => navigation.navigate('signup')}
                  variant="ghost"
                  size="small"
                  style={styles.footerLinkBtn}
                  textStyle={[styles.footerLinkText, { color: colors.accent }]}
                />
              </View>
            </View>
          </Animated.View>
    </AuthSplitLayout>
  );
};

/* ── Styles ────────────────────────────────────────────────────────────── */
const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 24px 60px -18px rgba(15,23,42,0.22), 0 6px 18px -8px rgba(15,23,42,0.10)' } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 14 },
          shadowOpacity: 0.14,
          shadowRadius: 32,
          elevation: 10,
        }),
  },
  welcomeTitle: {
    fontSize: 32,
    fontWeight: '900',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: -1,
    lineHeight: 40,
  },
  welcomeSub: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 26,
    textAlign: 'center',
    lineHeight: 22,
    letterSpacing: 0.1,
  },
  form: {
    width: '100%',
  },
  forgotRow: {
    marginBottom: 16,
    alignItems: 'flex-end',
  },
  forgotBtn: {
    width: 'auto' as any,
    alignSelf: 'flex-end',
    marginTop: -8,
    minHeight: 'auto' as any,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
  },
  forgotBtnText: {
    textTransform: 'none' as any,
    fontWeight: '700',
    fontSize: 13.5,
    letterSpacing: 0.2,
  },
  signInBtn: {
    marginTop: 4,
    height: 52,
    minHeight: 52,
    borderRadius: 14,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 14px 28px rgba(11,11,13,0.32)' } as any)
      : { shadowColor: '#0B0B0D', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.32, shadowRadius: 22, elevation: 10 }),
  },
  signInBtnText: {
    fontSize: 15,
    letterSpacing: 1.2,
  },
  footerDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 14,
    gap: 12,
  },
  footerRule: { flex: 1, height: 1 },
  footerDividerText: {
    fontSize: 10.5,
    fontWeight: '800',
    letterSpacing: 1.2,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 14,
    fontWeight: '500',
  },
  footerLinkBtn: {
    width: 'auto' as any,
    minHeight: 'auto' as any,
    paddingHorizontal: 0,
  },
  footerLinkText: {
    textTransform: 'none' as any,
    fontWeight: '700',
    fontSize: 14,
    letterSpacing: 0,
  },
});

export default Login_Screen;
